import React, { useContext } from "react";
import { Divider, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation,
  PAGE_ROUTE_BLOG,
  PAGE_ROUTE_CONTACT
} from "@kivunova/kivufrontendcommon";
import PublicInfoCallout from "./PublicInfoCallout";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import {
  LearnMoreCtaSection,
  LearnMoreParagraphs,
  LearnMoreSectionsStack
} from "./LearnMoreContentViews";
import {
  BLOG_INTRO_PARAGRAPHS,
  getBlogSectionsByIds
} from "./constants/blogContent";
import {
  getLearnMoreTopicById,
  LEARN_MORE_TOPIC_RELATED_LINKS
} from "./constants/learnMoreTopicContent";
import {
  publicInfoBodySx,
  publicInfoLinkSx
} from "./constants/publicInfoPageStyles";

type LearnMoreTopicPageProps = {
  topicId: string;
};

const LearnMoreTopicPage: React.FC<LearnMoreTopicPageProps> = ({ topicId }) => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const topic = getLearnMoreTopicById(topicId);

  const pathFor = (route?: string) => {
    if (!route) {
      return "/";
    }
    return language != null && defaultLang != null
      ? localizedPath(route, language, defaultLang)
      : `/${route}`;
  };

  if (!topic) {
    return null;
  }

  const sections = getBlogSectionsByIds(topic.sectionIds);
  const relatedLinks = LEARN_MORE_TOPIC_RELATED_LINKS[topic.id] ?? [];
  const hubPath = pathFor(PAGE_ROUTE_BLOG);
  const contactPath = pathFor(PAGE_ROUTE_CONTACT);

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId={`learn-more-topic-${topic.id}`}
        eyebrow={t("learn-more-topic-eyebrow", "Learn more")}
        title={t(topic.titleKey, topic.titleFallback)}
        intro={t(topic.introKey, topic.introFallback)}
      />

      {topic.calloutIngress ? (
        <PublicInfoCallout testId={`learn-more-topic-${topic.id}-callout`}>
          {t(
            topic.calloutIngress.prefixKey,
            topic.calloutIngress.prefixFallback
          )}
          <MuiLink
            component={Link}
            to={pathFor(topic.calloutIngress.route)}
            underline="hover"
            sx={publicInfoLinkSx}
            data-testid={topic.calloutIngress.testId}
          >
            {t(topic.calloutIngress.linkKey, topic.calloutIngress.linkFallback)}
          </MuiLink>
          {t(
            topic.calloutIngress.suffixKey,
            topic.calloutIngress.suffixFallback
          )}
          {topic.calloutIngressSecondary ? (
            <>
              {t(
                topic.calloutIngressSecondary.prefixKey,
                topic.calloutIngressSecondary.prefixFallback
              )}
              <MuiLink
                component={Link}
                to={pathFor(topic.calloutIngressSecondary.route)}
                underline="hover"
                sx={publicInfoLinkSx}
                data-testid={topic.calloutIngressSecondary.testId}
              >
                {t(
                  topic.calloutIngressSecondary.linkKey,
                  topic.calloutIngressSecondary.linkFallback
                )}
              </MuiLink>
              {t(
                topic.calloutIngressSecondary.suffixKey,
                topic.calloutIngressSecondary.suffixFallback
              )}
            </>
          ) : null}
        </PublicInfoCallout>
      ) : topic.calloutFallback ? (
        <PublicInfoCallout testId={`learn-more-topic-${topic.id}-callout`}>
          {t(topic.calloutKey ?? topic.introKey, topic.calloutFallback)}
        </PublicInfoCallout>
      ) : null}

      <Stack
        spacing={1.5}
        sx={{
          mb: 3,
          mt: topic.calloutIngress || topic.calloutFallback ? 0 : 2
        }}
      >
        {topic.includeIntroParagraphs ? (
          <LearnMoreParagraphs items={BLOG_INTRO_PARAGRAPHS} />
        ) : null}
      </Stack>

      <LearnMoreSectionsStack sections={sections} />

      {relatedLinks.length > 0 ? (
        <PublicInfoCtaPanel
          title={t("learn-more-topic-related-title", "What to read next")}
          testId={`learn-more-topic-${topic.id}-related`}
        >
          <Typography variant="body1" sx={publicInfoBodySx}>
            {relatedLinks.map((link, index) => (
              <React.Fragment key={link.labelKey}>
                {index > 0 ? t("learn-more-topic-related-mid", " · ") : null}
                <MuiLink
                  component={Link}
                  to={pathFor(link.route)}
                  underline="hover"
                  sx={publicInfoLinkSx}
                >
                  {t(link.labelKey, link.labelFallback)}
                </MuiLink>
              </React.Fragment>
            ))}
            {t("learn-more-topic-related-mid", " · ")}{" "}
            <MuiLink
              component={Link}
              to={hubPath}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t("learn-more-topic-related-hub", "All topics")}
            </MuiLink>
          </Typography>
        </PublicInfoCtaPanel>
      ) : (
        <PublicInfoCtaPanel
          title={t("learn-more-topic-back-title", "Explore more")}
          testId={`learn-more-topic-${topic.id}-back`}
        >
          <Typography variant="body1" sx={publicInfoBodySx}>
            <MuiLink
              component={Link}
              to={hubPath}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t("learn-more-topic-back-hub", "Back to all learn more topics")}
            </MuiLink>
            {t("learn-more-topic-related-mid", " · ")}{" "}
            <MuiLink
              component={Link}
              to={contactPath}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t("learn-more-topic-back-contact", "Contact us")}
            </MuiLink>
          </Typography>
        </PublicInfoCtaPanel>
      )}

      {topic.id === "benefits" ? (
        <>
          <Divider sx={{ my: 4 }} />
          <LearnMoreCtaSection />
        </>
      ) : null}
    </PublicMarketingLayout>
  );
};

export default LearnMoreTopicPage;
